<template>
  <div class="roadmap pa-10">
    <div class="roadmap-header">
      <h1 class="roadmap-title">Roadmap</h1>
      <p class="roadmap-subtitle">
        Vote for backlog items you’d like to see prioritized. Your vote helps us
        decide what to build next.
      </p>
    </div>

    <div v-if="loading" class="roadmap-loading">Loading…</div>

    <template v-else>
      <!-- Currently in development (no voting) -->
      <section v-if="inDevelopment.length > 0" class="roadmap-section">
        <h2 class="roadmap-section-title">Currently in development</h2>
        <ul class="roadmap-list">
          <li
            v-for="item in inDevelopment"
            :key="'dev-' + item.uid"
            class="roadmap-row"
            :class="{ 'roadmap-row--expanded': expandedId === item.uid }"
          >
            <button
              type="button"
              class="roadmap-row-trigger"
              :aria-expanded="expandedId === item.uid"
              @click="toggle(item.uid)"
            >
              <span class="roadmap-row-title">{{
                item.title || "Untitled"
              }}</span>
              <span class="roadmap-row-meta">
                <span class="roadmap-row-badge">In progress</span>
                <span class="roadmap-row-chevron" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </span>
            </button>
            <div v-show="expandedId === item.uid" class="roadmap-row-content">
              <p class="roadmap-row-description">
                {{ item.description || "No description." }}
              </p>
            </div>
          </li>
        </ul>
      </section>

      <!-- Backlog (with voting) -->
      <section class="roadmap-section">
        <h2 class="roadmap-section-title">
          Backlog
          <span style="font-size: 12px; color: #737373; font-weight: 400"
            >(please vote here!)</span
          >
        </h2>
        <ul v-if="backlog.length === 0" class="roadmap-empty">
          <li>
            No backlog items right now. Check back later or suggest something.
          </li>
        </ul>
        <ul v-else class="roadmap-list">
          <li
            v-for="item in backlog"
            :key="item.uid"
            class="roadmap-row"
            :class="{ 'roadmap-row--expanded': expandedId === item.uid }"
          >
            <button
              type="button"
              class="roadmap-row-trigger"
              :aria-expanded="expandedId === item.uid"
              @click="toggle(item.uid)"
            >
              <span class="roadmap-row-title">{{
                item.title || "Untitled"
              }}</span>
              <span class="roadmap-row-meta">
                <span
                  class="roadmap-votes"
                  :class="{ 'roadmap-votes--voted': hasVoted(item) }"
                >
                  <button
                    type="button"
                    class="roadmap-vote-btn"
                    :aria-pressed="hasVoted(item)"
                    :disabled="votingId === item.uid"
                    @click.stop="toggleVote(item)"
                  >
                    <svg
                      class="roadmap-vote-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                    <span class="roadmap-vote-count">{{
                      voteCount(item)
                    }}</span>
                  </button>
                </span>
                <span class="roadmap-row-chevron" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
              </span>
            </button>
            <div v-show="expandedId === item.uid" class="roadmap-row-content">
              <p class="roadmap-row-description">
                {{ item.description || "No description." }}
              </p>
            </div>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import { db } from "@/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { mapState } from "vuex";

export default {
  name: "SettingsRoadmap",
  data() {
    return {
      roadmap: [],
      expandedId: null,
      loading: true,
      votingId: null,
    };
  },
  computed: {
    ...mapState({
      userProfile: (state) => state.users.userProfile,
    }),
    inDevelopment() {
      return this.roadmap.filter((item) => item.status === "in development");
    },
    backlog() {
      return this.roadmap.filter((item) => item.status === "backlog");
    },
  },
  mounted() {
    this.getRoadmap();
  },
  methods: {
    async getRoadmap() {
      this.loading = true;
      const q = query(
        collection(db, "backlog"),
        where("type", "==", "Feature"),
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => ({ ...d.data(), uid: d.id }));
      // Sort by vote count (Firestore can't orderBy array length)
      this.roadmap = items.sort(
        (a, b) => (b.votes?.length ?? 0) - (a.votes?.length ?? 0),
      );

      this.loading = false;
    },
    toggle(uid) {
      this.expandedId = this.expandedId === uid ? null : uid;
    },
    voteCount(item) {
      const votes = item.votes || [];
      return Array.isArray(votes) ? votes.length : 0;
    },
    hasVoted(item) {
      if (!this.userProfile?.uid) return false;
      const votes = item.votes || [];
      return Array.isArray(votes) && votes.includes(this.userProfile.uid);
    },
    async toggleVote(item) {
      if (!this.userProfile?.uid || !item.uid) return;
      this.votingId = item.uid;
      const votes = item.votes || [];
      const hasVoted =
        Array.isArray(votes) && votes.includes(this.userProfile.uid);
      const ref = doc(db, "backlog", item.uid);
      try {
        if (hasVoted) {
          await updateDoc(ref, { votes: arrayRemove(this.userProfile.uid) });
          const idx = this.roadmap.findIndex((i) => i.uid === item.uid);
          if (idx !== -1) {
            const next = { ...this.roadmap[idx] };
            next.votes = (next.votes || []).filter(
              (id) => id !== this.userProfile.uid,
            );
            this.roadmap.splice(idx, 1, next);
          }
        } else {
          await updateDoc(ref, { votes: arrayUnion(this.userProfile.uid) });
          const idx = this.roadmap.findIndex((i) => i.uid === item.uid);
          if (idx !== -1) {
            const next = { ...this.roadmap[idx] };
            next.votes = [...(next.votes || []), this.userProfile.uid];
            this.roadmap.splice(idx, 1, next);
          }
        }
      } finally {
        this.votingId = null;
      }
    },
  },
};
</script>

<style scoped>
.roadmap {
  max-width: 640px;
}

.roadmap-header {
  margin-bottom: 1.5rem;
}

.roadmap-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  color: #0a0a0a;
  margin: 0 0 0.25rem 0;
}

.roadmap-subtitle {
  font-size: 0.875rem;
  color: #737373;
  margin: 0;
  line-height: 1.5;
}

.roadmap-loading,
.roadmap-empty {
  font-size: 0.875rem;
  color: #737373;
  padding: 1.5rem;
  border: 1px dashed #e5e5e5;
  border-radius: 0.5rem;
  background: #fafafa;
}

.roadmap-section {
  margin-bottom: 1.75rem;
}

.roadmap-section:last-child {
  margin-bottom: 0;
}

.roadmap-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #525252;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.01em;
}

.roadmap-empty {
  list-style: none;
  margin: 0;
  padding: 1.5rem;
}

.roadmap-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.roadmap-row {
  border: 1px solid #e5e5e5;
  border-radius: 0.5rem;
  background: #fff;
  overflow: hidden;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;
}

.roadmap-row:hover {
  border-color: #d4d4d4;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.roadmap-row--expanded {
  border-color: #d4d4d4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.roadmap-row-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  text-align: left;
  font: inherit;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  min-height: 2.75rem;
}

.roadmap-row-trigger:hover {
  background: #fafafa;
}

.roadmap-row-trigger:focus-visible {
  box-shadow: inset 0 0 0 2px #0a0a0a;
}

.roadmap-row-title {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #0a0a0a;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.roadmap-row-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.roadmap-row-badge {
  font-size: 0.75rem;
  font-weight: 500;
  color: #737373;
  background: #f5f5f5;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
}

.roadmap-votes {
  display: flex;
  align-items: center;
}

.roadmap-vote-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #737373;
  background: #f5f5f5;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s,
    border-color 0.15s;
}

.roadmap-vote-btn:hover:not(:disabled) {
  color: #0a0a0a;
  background: #e5e5e5;
}

.roadmap-vote-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.roadmap-vote-btn[aria-pressed="true"],
.roadmap-votes--voted .roadmap-vote-btn {
  color: #0a0a0a;
  background: #e5e5e5;
  border-color: #d4d4d4;
}

.roadmap-vote-icon {
  flex-shrink: 0;
}

.roadmap-vote-count {
  min-width: 1.25rem;
}

.roadmap-row-chevron {
  color: #a3a3a3;
  transition: transform 0.2s ease;
}

.roadmap-row--expanded .roadmap-row-chevron {
  transform: rotate(180deg);
}

.roadmap-row-content {
  padding: 0 1rem 1rem;
  border-top: 1px solid #f5f5f5;
}

.roadmap-row-description {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: #525252;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
